import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Divider,
  Button,
  TextField,
  MenuItem,
  Stack,
  Link,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import { UpdateOrganization } from '../../Api/CreateOrganization';
import { ConnectStripeCustomer } from '../../Api/Billing';
import { listSignupCodes } from '../../Api/SignupCodes';
import { hasTechAdminAccess } from '../../Utils/adminUserTypes';
import { computeAccessWindow } from '../../Utils/frameworkTiers';
import StandardCollapsibleSection from '../StandardComponents/StandardCollapsibleSection/StandardCollapsibleSection';

const statusColor = (status) => {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'info';
    case 'past_due': return 'warning';
    case 'canceled': return 'error';
    default: return 'default';
  }
};

const fmtDate = (unix) => {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleDateString();
};

const boolLabel = (v) => (v ? 'Yes' : 'No');

// Fields editable from the Subscription Overview surface (org column names).
const EDITABLE_FIELDS = [
  { key: 'organization_name', label: 'Organization Name' },
  { key: 'entity_name', label: 'Entity Name' },
  { key: 'incorporation_number', label: 'UEN / Inc. Number' },
  { key: 'industry', label: 'Industry' },
  { key: 'country', label: 'Country' },
  { key: 'website', label: 'Website' },
  { key: 'phone_number', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'postal_code', label: 'Postal Code' },
  { key: 'block', label: 'Block' },
  { key: 'street_name', label: 'Street' },
  { key: 'level_no', label: 'Level' },
  { key: 'unit_no', label: 'Unit' },
  { key: 'building_name', label: 'Building' },
];

function DetailGrid({ rows }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: 1.25 }}>
      {rows.map(({ label, value, mono }) => (
        <React.Fragment key={label}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {typeof value === 'object' && value !== null && !Array.isArray(value) ? (
            value
          ) : (
            <Typography
              variant="body2"
              sx={{ fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}
            >
              {value ?? '—'}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

function MetadataTable({ metadata }) {
  const entries = Object.entries(metadata || {}).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return <Typography variant="body2" color="text.secondary">No metadata fields.</Typography>;
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '38%', fontWeight: 600 }}>Key</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([key, value]) => (
            <TableRow key={key} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', verticalAlign: 'top' }}>{key}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', wordBreak: 'break-word', verticalAlign: 'top' }}>{value || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function SubscriptionOverviewPanel({ loading, error, notice, overview, orgName, org, onReload }) {
  const isTechAdmin = hasTechAdminAccess();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [signupCodeId, setSignupCodeId] = useState('');
  const [signupOptions, setSignupOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  const zavior = overview?.zavior_org;
  const signup = overview?.signup_source;
  const effectiveTiers = overview?.effective_tiers || [];
  // buildPartialOverview (404 fallback) omits stripe_payload; edit needs the full overview.
  const isPartial = overview ? !overview.stripe_payload : false;

  const access = zavior ? computeAccessWindow(zavior) : null;
  // is_paid_client should correlate with holding a paid tier; flag the two inconsistent states.
  const paidNoTier = !!zavior?.is_paid_client && effectiveTiers.length === 0;
  const tierNoAccess = effectiveTiers.length > 0 && access && !access.isActive;

  // Load signup code options once for the tech-admin dropdown.
  useEffect(() => {
    if (!isTechAdmin) return;
    listSignupCodes()
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? [];
        setSignupOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => setSignupOptions([]));
  }, [isTechAdmin]);

  // Reset edit state whenever the selected org changes.
  useEffect(() => {
    setEditing(false);
    setDraft({});
    setSignupCodeId(zavior?.signup_source_code_id || org?.signup_source_code_id || '');
  }, [zavior?.id, org?.id, zavior?.signup_source_code_id, org?.signup_source_code_id]);

  const startEdit = () => {
    const seed = {};
    EDITABLE_FIELDS.forEach(({ key }) => { seed[key] = (org?.[key] ?? zavior?.[key] ?? '') || ''; });
    seed.description = (org?.description ?? zavior?.description ?? '') || '';
    setDraft(seed);
    setSignupCodeId(zavior?.signup_source_code_id || org?.signup_source_code_id || '');
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraft({}); };

  const handleSave = async () => {
    if (!org?.id) { toast.error('Missing organization context'); return; }
    setSaving(true);
    try {
      // Spread the full org row so the full-struct PUT does not zero unsent columns.
      const payload = { ...org, ...draft, description: draft.description };
      if (isTechAdmin) payload.signup_source_code_id = signupCodeId || null;
      await UpdateOrganization(org.id, JSON.stringify(payload));
      // Push updated profile to the linked Stripe customer, if any.
      if (org.stripe_customer_id) {
        await ConnectStripeCustomer(org.id, org.stripe_customer_id).catch(() => null);
      }
      toast.success('Organization updated');
      setEditing(false);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const signupCodeLabel = useMemo(() => {
    const opt = signupOptions.find((c) => c.id === signupCodeId);
    return opt ? (opt.display_name || opt.code) : '';
  }, [signupOptions, signupCodeId]);

  if (loading) return <CircularProgress sx={{ mt: 2 }} />;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  if (!overview) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Select an Organization to view its subscription status.
      </Typography>
    );
  }

  const { subscription: sub, stripe_customer: stripe, stripe_payload: payload } = overview;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{orgName}</Typography>

      {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}

      {/* ── Zavior (our records) — view / edit ── */}
      <StandardCollapsibleSection
        title="Zavior (Our Records)"
        subtitle="Organization data stored in Zavior for this customer."
        icon={BusinessOutlinedIcon}
        action={
          isPartial ? (
            <Alert severity="info" sx={{ py: 0 }}>Overview API not deployed — view only.</Alert>
          ) : !editing ? (
            <Button variant="outlined" className="global-outlined-button" size="small" startIcon={<EditIcon />} onClick={startEdit}>
              Edit
            </Button>
          ) : null
        }
      >
        {paidNoTier && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Marked as a paid client but holds no active paid tier. Grant a tier or clear the paid flag so access matches what they're entitled to.
          </Alert>
        )}
        {tierNoAccess && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Holds paid tier(s) but platform access has lapsed{access?.activeUntil ? ` (ended ${access.activeUntil.toLocaleDateString()})` : ''} — users can't log in. Extend access in the Entitlements tab or mark the org paid.
          </Alert>
        )}
        {editing ? (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {EDITABLE_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key} size="small" label={label} value={draft[key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                />
              ))}
            </Box>
            <TextField
              size="small" fullWidth multiline minRows={2} label="Description" sx={{ mt: 2 }}
              value={draft.description ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" className="global-primary-button" onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
                Save
              </Button>
              <Button className="global-text-button" onClick={cancelEdit} disabled={saving}>Cancel</Button>
            </Stack>
          </>
        ) : (
          <DetailGrid rows={[
            { label: 'Organization ID', value: zavior?.id, mono: true },
            { label: 'Organization Name', value: zavior?.organization_name },
            { label: 'Entity Name', value: zavior?.entity_name },
            { label: 'UEN / Inc. Number', value: zavior?.incorporation_number },
            { label: 'Industry', value: zavior?.industry },
            { label: 'Country', value: zavior?.country },
            { label: 'Website', value: zavior?.website },
            { label: 'Phone', value: zavior?.phone_number },
            { label: 'Address', value: [zavior?.address, zavior?.building_name, zavior?.postal_code].filter(Boolean).join(', ') || '—' },
            { label: 'Description', value: zavior?.description },
            { label: 'Billing Contact', value: [zavior?.billing_contact_name, zavior?.billing_contact_email].filter(Boolean).join(' · ') || '—' },
            { label: 'Stripe Customer ID (DB)', value: zavior?.stripe_customer_id, mono: true },
            { label: 'Paid Client (DB)', value: boolLabel(zavior?.is_paid_client) },
            {
              label: 'Platform Access',
              value: (
                <Chip
                  size="small"
                  variant="outlined"
                  color={access?.isActive ? 'success' : 'error'}
                  sx={{ width: 'fit-content' }}
                  label={
                    access?.isPaidOverride
                      ? 'Paid client (override)'
                      : access?.isActive
                        ? `Active until ${access.activeUntil ? access.activeUntil.toLocaleDateString() : '—'}`
                        : access?.activeUntil ? `Expired ${access.activeUntil.toLocaleDateString()}` : 'No access window'
                  }
                />
              ),
            },
            { label: 'Onboarding Complete', value: boolLabel(zavior?.onboarding_completed) },
            { label: 'Org Status', value: zavior?.org_status },
            { label: 'Created', value: zavior?.created_at },
          ]} />
        )}
      </StandardCollapsibleSection>

      {/* ── Sign up source ── */}
      <StandardCollapsibleSection
        title="Sign Up Source"
        subtitle="Marketing/partner code attributed to this organization at registration."
        icon={LinkOutlinedIcon}
      >
        {!signup && !editing ? (
          <Alert severity="info" sx={{ mb: 0 }}>No signup source code — direct registration (Default).</Alert>
        ) : (
          <>
            {editing && isTechAdmin && (
              <TextField
                select size="small" label="Signup Source" value={signupCodeId}
                onChange={(e) => setSignupCodeId(e.target.value)}
                sx={{ mb: 2, minWidth: 280 }}
                helperText="Permanent attribution unless changed here."
              >
                <MenuItem value="">Default (none)</MenuItem>
                {signupOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.display_name || c.code}</MenuItem>
                ))}
              </TextField>
            )}
            {signup && (
              <DetailGrid rows={[
                { label: 'Code', value: signup.code, mono: true },
                { label: 'Display Name', value: signup.display_name },
                { label: 'Description', value: signup.description },
                { label: 'Onboarding Route', value: signup.onboarding_route },
                { label: 'Org Onboarding Flow', value: signup.org_onboarding_flow },
                { label: 'Welcome Message', value: signup.welcome_message },
                { label: 'Logo URL', value: signup.logo_url, mono: true },
                { label: 'Default Country', value: signup.default_country },
                { label: 'Code Active', value: boolLabel(signup.is_active) },
                ...(signup.attribution ? [
                  { label: 'Registered At', value: signup.attribution.registered_at ? new Date(signup.attribution.registered_at).toLocaleString() : '—' },
                  { label: 'Source Method', value: signup.attribution.source_method },
                  { label: 'Referrer', value: signup.attribution.referrer },
                  { label: 'OTP Sent', value: signup.attribution.otp_sent_at ? new Date(signup.attribution.otp_sent_at).toLocaleString() : '—' },
                  { label: 'OTP Verified', value: signup.attribution.otp_verified_at ? new Date(signup.attribution.otp_verified_at).toLocaleString() : '—' },
                ] : []),
              ]} />
            )}
            {editing && isTechAdmin && !signup && signupCodeLabel && (
              <Typography variant="body2" color="text.secondary">Will attribute to: {signupCodeLabel}</Typography>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Link href={process.env.REACT_APP_PORTAL_MODE === 'trinity' ? '/signup-codes' : '/adminlist?view=SignupCodes'} target="_blank" rel="noopener" sx={{ fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Manage Codes <OpenInNewIcon sx={{ fontSize: 13 }} />
              </Link>
            </Box>
          </>
        )}
      </StandardCollapsibleSection>

      {/* ── Stripe customer (live) ── */}
      <StandardCollapsibleSection
        title="Stripe Customer (Live)"
        subtitle="Current customer record returned by the Stripe API."
        icon={PaymentOutlinedIcon}
      >
        {!stripe ? (
          <Alert severity="info" sx={{ mb: 0 }}>
            No Stripe customer linked yet. Connect or auto-create a customer from the Customer Management tab.
          </Alert>
        ) : (
          <>
            <DetailGrid rows={[
              { label: 'Customer ID', value: stripe.id, mono: true },
              { label: 'Name', value: stripe.name },
              { label: 'Email', value: stripe.email },
              { label: 'Phone', value: stripe.phone },
              { label: 'Description', value: stripe.description },
              { label: 'Created in Stripe', value: fmtDate(stripe.created) },
            ]} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Metadata on Stripe</Typography>
            <MetadataTable metadata={stripe.metadata} />
          </>
        )}
      </StandardCollapsibleSection>

      {/* ── Payload sent to Stripe ── */}
      <StandardCollapsibleSection
        title="Payload Sent to Stripe"
        subtitle="Fields and metadata Zavior maps from this organization when creating or syncing a Stripe customer."
        icon={SendOutlinedIcon}
      >
        <DetailGrid rows={[
          { label: 'Name', value: payload?.name },
          { label: 'Email', value: payload?.email },
          { label: 'Phone', value: payload?.phone },
          { label: 'Description', value: payload?.description },
          { label: 'Preferred Locales', value: (payload?.preferred_locales || []).join(', ') || '—' },
          {
            label: 'Address',
            value: payload?.address ? Object.entries(payload.address).map(([k, v]) => `${k}: ${v}`).join(' · ') : '—',
          },
        ]} />
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Metadata Payload</Typography>
        <MetadataTable metadata={payload?.metadata} />
      </StandardCollapsibleSection>

      {/* ── Subscriptions ── */}
      <StandardCollapsibleSection
        title="Subscriptions"
        subtitle="Active or trialing subscriptions for this Stripe customer."
        icon={AutorenewOutlinedIcon}
      >
        {!sub?.stripe_customer_id ? (
          <Typography variant="body2" color="text.secondary">Link a Stripe customer to view subscriptions.</Typography>
        ) : (
          <>
            <DetailGrid rows={[
              {
                label: 'Overall Status',
                value: (
                  <Chip label={sub.status || 'none'} size="small" color={statusColor(sub.status)} variant="outlined" sx={{ width: 'fit-content' }} />
                ),
              },
              { label: 'Paid Client (Synced)', value: boolLabel(sub.is_paid_client) },
            ]} />
            {(sub.subscriptions || []).length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Subscription</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Plan</strong></TableCell>
                      <TableCell><strong>Tier</strong></TableCell>
                      <TableCell><strong>Period End</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sub.subscriptions.map((row) => (
                      <TableRow key={row.subscription_id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.subscription_id}</TableCell>
                        <TableCell>
                          <Chip label={row.status} size="small" color={statusColor(row.status)} variant="outlined" />
                        </TableCell>
                        <TableCell>{row.plan_name || '—'}</TableCell>
                        <TableCell>{row.tier || '—'}</TableCell>
                        <TableCell>{fmtDate(row.current_period_end)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Stripe customer is linked but no active or trialing subscriptions were found.
              </Alert>
            )}
          </>
        )}
      </StandardCollapsibleSection>
    </Box>
  );
}
